import ShareDialog from '@components/share';
import useProcess from '@hooks/useProcess';
import type { ProcessModel } from '@models/process.model';
import type { StudioUser } from '@stratumn/studio-shared';
import {
  AlertTriangle,
  ChevronsUpDown,
  ExternalLink,
  Rocket,
  Share2
} from 'lucide-react';
import { observer } from 'mobx-react';
import { useState } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { TRACE_UI_URL } from '@/utils/constants';

type HeaderNavRightProps = {
  currentProcessId: string;
  can_edit?: boolean;
  hasBeenGenerated: boolean | undefined;
  workflowList: ProcessModel['workflows'];
  user: StudioUser;
};

const HeaderNavRight = ({
  currentProcessId,
  can_edit,
  hasBeenGenerated,
  workflowList,
  user
}: HeaderNavRightProps) => {
  const process = useProcess();
  const generability = process?.isProcessGenerable;
  const canGenerate = generability?.generable ?? true;
  const [openModalShare, setOpenModalShare] = useState(false);
  const navigate = useNavigate();
  const isOnNewGenerationPage = !!useMatch('/:processId/generation/new');

  return (
    <div className="flex items-center gap-3">
      {can_edit && !isOnNewGenerationPage && (
        <HoverCard closeDelay={100} openDelay={0}>
          <HoverCardTrigger asChild>
            <span
              className={`inline-flex ${canGenerate ? '' : 'cursor-not-allowed'}`}
            >
              <Button
                disabled={!canGenerate}
                onClick={() => {
                  navigate(`/${currentProcessId}/generation/new`);
                }}
              >
                <Rocket size={16} />
                Generate
              </Button>
            </span>
          </HoverCardTrigger>
          {!canGenerate &&
            generability?.reasons &&
            generability.reasons.length > 0 && (
              <HoverCardContent
                className="w-auto max-w-xs p-3"
                side="bottom"
                sideOffset={8}
              >
                {generability.reasons.length === 1 ? (
                  <p className="flex items-start gap-2 text-muted-foreground text-sm">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    {generability.reasons[0]}
                  </p>
                ) : (
                  <div className="flex items-start gap-2 text-muted-foreground text-sm">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-500" />
                    <ul className="list-disc pl-4">
                      {generability.reasons.map(reason => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </HoverCardContent>
            )}
        </HoverCard>
      )}

      {hasBeenGenerated &&
        (() => {
          const publishedWorkflows = workflowList.filter(
            wf => wf.published_id !== null
          );
          if (publishedWorkflows.length === 0) {
            return null;
          }
          if (publishedWorkflows.length === 1) {
            return (
              <Button asChild variant="outline">
                <Link
                  target="_blank"
                  to={`${TRACE_UI_URL}/workflow/${publishedWorkflows[0].published_id}`}
                >
                  Trace
                  <ExternalLink size={14} />
                </Link>
              </Button>
            );
          }
          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline">
                  Trace
                  <ChevronsUpDown size={14} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto min-w-[200px] p-1">
                <div className="flex flex-col">
                  {publishedWorkflows.map(wf => (
                    <Link
                      className="flex items-center gap-2 rounded-sm px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground"
                      key={wf.id}
                      target="_blank"
                      to={`${TRACE_UI_URL}/workflow/${wf.published_id}`}
                    >
                      <ExternalLink size={14} />
                      {wf.name}
                    </Link>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          );
        })()}

      <Button
        className="size-8"
        onClick={() => {
          setOpenModalShare(!openModalShare);
        }}
        size="icon"
        variant="ghost"
      >
        <Share2 size={16} />
      </Button>

      {currentProcessId && (
        <ShareDialog
          key="share"
          openModalShare={openModalShare}
          processId={currentProcessId}
          setOpenModalShare={setOpenModalShare}
          user={user}
        />
      )}
    </div>
  );
};

export default observer(HeaderNavRight);
